import FluxUtils from 'flux/utils';
import Immutable from 'immutable';

import type { ActionType } from 'sharedreact/actions/generic/actions';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';

import LoadHistoryActions from 'workspaces/launcher/actions/LoadHistoryActions';
import LoadHistoryConstants from 'workspaces/launcher/constants/LoadHistoryConstants';
import LoadHistoryDataType from 'workspaces/launcher/constants/LoadHistoryDataType';

const Content: {
  queueItems: Immutable.List<any>,
  userItems: Immutable.List<any>,
  isFetchingQueue: boolean,
  isFetchingUsers: boolean,
  usersFetchId: string | null,
  queueFetchId: string | null,
  range: string | null,
  requestedStart: number | null,
  requestedEnd: number | null,
  userInfo: Immutable.Map<string, any>,
  queueInfo: Immutable.Map<string, any>,
  maxSamples: number,
  sampleInterval: number
} = {
  queueItems: Immutable.List(),
  isFetchingQueue: false,
  queueFetchId: null,
  userItems: Immutable.List(),
  isFetchingUsers: false,
  usersFetchId: null,
  range: null,
  requestedStart: null,
  requestedEnd: null,
  userInfo: Immutable.Map(),
  queueInfo: Immutable.Map(),
  maxSamples: 100,
  sampleInterval: 0
};

class State extends Immutable.Record(Content) {
}

class LoadHistoryStore extends FluxUtils.ReduceStore<State, ActionType> {
  constructor () {
    super(AppDispatcher);
  }

  override getInitialState (): State {
    return new State();
  }

  getItems (what: string): Immutable.List<any> {
    if (what === LoadHistoryDataType.USER_DATA) {
      return this.getState().userItems;
    } else {
      return this.getState().queueItems;
    }
  }

  isFetching (what: string): boolean {
    if (what === LoadHistoryDataType.USER_DATA) {
      return this.getState().isFetchingUsers;
    } else {
      return this.getState().isFetchingQueue;
    }
  }

  getInfo (what: string, key: string): any {
    if (what === LoadHistoryDataType.USER_DATA) {
      return this.getState().userInfo.get(key);
    } else {
      return this.getState().queueInfo.get(key);
    }
  }

  getRange (): string |  null {
    return this.getState().range;
  }

  _getViewportStart (): number {
    const viewportStart1 = this.getState().userInfo.get('startViewport');
    const viewportStart2 = this.getState().queueInfo.get('startViewport');
    if (viewportStart1) {
      if (viewportStart2) {
        return Math.min(viewportStart1, viewportStart2);
      } else {
        return viewportStart1;
      }
    } else {
      return viewportStart2;
    }
  }

  _getViewportEnd (): number {
    const viewportEnd1 = this.getState().userInfo.get('endViewport');
    const viewportEnd2 = this.getState().queueInfo.get('endViewport');
    if (viewportEnd1) {
      if (viewportEnd2) {
        return Math.max(viewportEnd1, viewportEnd2);
      } else {
        return viewportEnd1;
      }
    } else {
      return viewportEnd2;
    }
  }

  getCurrentViewport (): number[] {
    return [this._getViewportStart(), this._getViewportEnd()];
  }

  getRequestedViewport (): number[] {
    // requests can use 0 values to adjust to the current recorded values.
    const start = Math.max(this.getState().requestedStart ?? 0, this.getRecordingStart());
    const end = (this.getState().requestedEnd ?? 0) > 0 ? this.getState().requestedEnd! : this.getRecordingEnd();
    return [start, end];
  }

  getRecordingStart (): number {
    const recordingStart1 = this.getState().userInfo.get('startRecording');
    const recordingStart2 = this.getState().queueInfo.get('startRecording');
    if (recordingStart1) {
      if (recordingStart2) {
        return Math.min(recordingStart1, recordingStart2);
      } else {
        return recordingStart1;
      }
    } else {
      return recordingStart2;
    }
  }

  getRecordingEnd (): number {
    const recordingEnd1 = this.getState().userInfo.get('endRecording');
    const recordingEnd2 = this.getState().queueInfo.get('endRecording');
    if (recordingEnd1) {
      if (recordingEnd2) {
        return Math.max(recordingEnd1, recordingEnd2);
      } else {
        return recordingEnd1;
      }
    } else {
      return recordingEnd2;
    }
  }

  getRecordingInterval (): number[] {
    return [this.getRecordingStart(), this.getRecordingEnd()];
  }

  getMaxSamples (): number {
    return this.getState().maxSamples;
  }
  
  canZoomIn (): boolean {
    if (this.getState().userItems.size < 5) return false;  // graph needs a certain number of points
    if (this.getState().queueItems.size < 5) return false;  // graph needs a certain number of points
    return true;
  }

  // how to handle viewports - in theory we have 2 data sets with potentially different start/end
  // until UX of setting viewports is clear - just base on user data
  canZoomOut (): boolean {
    const [viewportStart, viewportEnd] = this.getCurrentViewport();
    if (viewportStart && viewportEnd) {
      const [recordingStart, recordingEnd] = this.getRecordingInterval();
      return (recordingStart < viewportStart || recordingEnd > viewportEnd);
    }
    return false;
  }

  canGoBackward (): boolean {
    const viewportStart = this._getViewportStart();
    if (viewportStart) {
      const recordingStart = this.getRecordingStart();
      return (recordingStart < viewportStart);
    }
    return false;
  }

  canGoForward (): boolean {
    const viewportEnd = this._getViewportEnd();
    if (viewportEnd) {
      const recordingEnd = this.getRecordingEnd();
      return (recordingEnd > viewportEnd);
    }
    return false;
  }

  override reduce (state: State, action: ActionType): State {
    switch (action.actionType) {
      case LoadHistoryConstants.CLEAR_ALL: {
        return this.getInitialState();
      }
      case LoadHistoryConstants.SET_MAX_SAMPLES: {
        return state.set('maxSamples', action.value);
      }
      case LoadHistoryConstants.FETCH_STARTED: {
        if (action.what === LoadHistoryDataType.USER_DATA) {
          state = state.set('userItems', Immutable.List()).set('isFetchingUsers', true);
        } else {
          state = state.set('queueItems', Immutable.List()).set('isFetchingQueue', true);
        }
        return state.set('range', null);
      }
      case LoadHistoryConstants.FETCH_RANGE_STARTED: {
        if (action.what === LoadHistoryDataType.USER_DATA) {
          return state.set('userItems', Immutable.List()).set('isFetchingUsers', true).set('range', action.range);
        } else {
          return state.set('queueItems', Immutable.List()).set('isFetchingQueue', true).set('range', action.range);
        }
      }
      case LoadHistoryConstants.GET_FAIL: {
        if (action.what === LoadHistoryDataType.USER_DATA) {
          return state.set('userItems', Immutable.List()).set('isFetchingUsers', false);
        } else {
          return state.set('queueItems', Immutable.List()).set('isFetchingQueue', false);
        }
      }
      case LoadHistoryConstants.GET_SUCCESS: {
        state = state.set('requestedStart', action.start).set('requestedEnd', action.end);
        const isComplete = action.fetchInfo.get('isComplete');
        if (action.what === LoadHistoryDataType.USER_DATA) {
          state = state.set('usersFetchId', action.fetchId);
          if (!isComplete) {
            setTimeout(() =>  LoadHistoryActions.fetchMore(LoadHistoryDataType.USER_DATA, action.fetchId, action.start, action.end, action.items.get(action.items.size-1).time, action.fetchInfo.get('sampleInterval')));
          }
          return state.set('userItems', action.items).set('isFetchingUsers', !isComplete).set('userInfo', action.fetchInfo);
        } else {
          state = state.set('queueFetchId', action.fetchId);
          if (!isComplete) {
            setTimeout(() =>  LoadHistoryActions.fetchMore(LoadHistoryDataType.QUEUE_DATA, action.fetchId, action.start, action.end, action.items.get(action.items.size-1).time, action.fetchInfo.get('sampleInterval')));
          }
          return state.set('queueItems', action.items).set('isFetchingQueue', !isComplete).set('queueInfo', action.fetchInfo);
        }
      }
      case LoadHistoryConstants.GETMORE_SUCCESS: {
        // check on fetchId to ignore continuation responses from old queries
        const isComplete = action.fetchInfo.get('isComplete');
         if (action.what === LoadHistoryDataType.USER_DATA) {
          if (state.get('usersFetchId') == action.fetchId)  {
            const userInfo: Immutable.Map<string, any> = action.fetchInfo;
            const startUserViewport = state.userInfo.get('startViewport');

            state = state
              .set('isFetchingUsers', !isComplete)
              .set('userInfo', userInfo
                .set('startViewport', startUserViewport)
            );
            if (!isComplete) {
              setTimeout(() =>  LoadHistoryActions.fetchMore(LoadHistoryDataType.USER_DATA, action.fetchId, action.start, action.end, action.items.get(action.items.size-1).time, action.fetchInfo.get('sampleInterval')));
            }
              return state.set('userItems', state.userItems.concat(action.items));
          }
        } else {
          if (state.get('queueFetchId') == action.fetchId)  {
            const queueInfo: Immutable.Map<string, any> = action.fetchInfo;
            const startQueueViewport = state.queueInfo.get('startViewport'); 

            state = state
              .set('isFetchingQueue', !isComplete)
              .set('queueInfo', queueInfo
                .set('startViewport', startQueueViewport)
            );

            if (!isComplete) {
              setTimeout(() =>  LoadHistoryActions.fetchMore(LoadHistoryDataType.QUEUE_DATA, action.fetchId, action.start, action.end, action.items.get(action.items.size-1).time, action.fetchInfo.get('sampleInterval')));
            }
             return state.set('queueItems', state.queueItems.concat(action.items));
          }
        }
      }
    }
    return state;
  }
}

export default new LoadHistoryStore();